import { Box, Container, Typography, alpha, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { getRelativeFontSize, mediumFont, regularFont } from "utils/styles";
import aboutImage from "../../assets/images/about-image-1.svg";
import { formatDescriptionMarkdown } from "../Blogs/formatDescriptionMarkdown";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

const PRIVACY_CONTENT = `IIS is committed to protecting the privacy of its users. This privacy policy explains how we collect, use, and protect your personal information. By using our website and/or mobile app, you consent to the collection and use of your information as described in this policy.

## Information We Collect

We may collect the following types of information from you:

*Personal information*: This includes your name, email address, phone number, and any other information you provide to us when you register for an account, or fill out a form.

*Health information*: This includes any information you provide to us about your health, medical history, or treatment. We will only collect this information if you choose to provide it to us and it is necessary to provide our services to you.

*Usage information*: This includes information about how you use our website and/or mobile app, such as the pages you visit, the links you click on, and the features you use. We may also collect information about your device, such as your IP address, browser type, and operating system.

## How We Use Your Information

We may use your information for the following purposes:

*To provide our services to you*: We may use your personal and health information to provide you with access to our programs, services, and resources.

*To improve our website and/or mobile app*: We may use your information to analyze how our website and/or mobile app is used and make improvements to the user experience.

*To communicate with you*: We may use your contact information to send you newsletters, updates, and other communications related to our organization.

*To comply with legal obligations*: We may disclose your information if required to do so by law or in response to a court order or subpoena.

## How We Protect Your Information

We take the security of your information seriously and have implemented technical and organizational measures to protect it from unauthorized access, use, or disclosure.

## How We Share Your Information

We may share your information with third parties in the following situations:

*With service providers*: We may share your information with third-party service providers who help us operate our website and/or mobile app or provide other services to us.

*With affiliates*: We may share your information with affiliated organizations that we work with.

*With your consent*: We may share your information with third parties if you have given us your consent to do so.

## Your Rights and Choices

You have certain rights with respect to your personal information, including:

*The right to access your information*: You have the right to request a copy of the personal information we hold about you.

*The right to correct your information*: You have the right to request that we correct any inaccurate or incomplete information we hold about you.

*The right to object to processing*: You have the right to object to the processing of your information in certain circumstances.

*The right to delete your information*: You have the right to request that we delete your personal information.

## Family Privacy

At IIS we understand that families may have unique privacy concerns related to their personal information. We take the privacy of all of our users seriously, including families and children under the age of 13.

## Changes to This Policy

We may update this privacy policy from time to time to reflect changes in our practices or legal requirements.`;

const PrivacyPolicy = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <>
      <style>
        {`
          @keyframes privacyPulse {
            0%, 100% { opacity: 0.85; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.03); }
          }
        `}
      </style>
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          backgroundColor: isDark
            ? theme.palette.background.default
            : alpha("#eef0f5", 1),
          pt: { xs: 10, md: 12 },
          pb: 10,
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Static background layer */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {/* Static visible background image */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${aboutImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: isDark ? 0.06 : 0.08,
              zIndex: 0,
            }}
            aria-hidden
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(BRAND, 0.15)} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, ${alpha(BRAND, 0.1)} 0%, transparent 50%)`
                : `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(BRAND, 0.1)} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, ${alpha(BRAND, 0.06)} 0%, transparent 50%)`,
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "8%",
              right: "5%",
              width: 360,
              height: 360,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(BRAND, isDark ? 0.2 : 0.12)} 0%, transparent 65%)`,
              filter: "blur(60px)",
              animation: "privacyPulse 10s ease-in-out infinite",
              zIndex: 2,
            }}
            aria-hidden
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "20%",
              left: "5%",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(BRAND, isDark ? 0.15 : 0.08)} 0%, transparent 65%)`,
              filter: "blur(50px)",
              animation: "privacyPulse 12s ease-in-out infinite 1.5s",
              zIndex: 2,
            }}
            aria-hidden
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `linear-gradient(${alpha(theme.palette.divider, isDark ? 0.08 : 0.1)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(theme.palette.divider, isDark ? 0.08 : 0.1)} 1px, transparent 1px)`,
              backgroundSize: "48px 48px",
              opacity: 0.8,
              zIndex: 2,
            }}
          />
        </Box>

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Hero section */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
              <Typography
                sx={{
                  ...mediumFont,
                  fontSize: getRelativeFontSize(28),
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                  position: "relative",
                  display: "inline-block",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 72,
                    height: 4,
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${BRAND}, ${BRAND_LIGHT})`,
                  },
                  [theme.breakpoints.down("md")]: {
                    fontSize: getRelativeFontSize(22),
                  },
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                sx={{
                  ...regularFont,
                  fontSize: getRelativeFontSize(2),
                  color: theme.palette.text.secondary,
                  mt: 2,
                  maxWidth: 560,
                  mx: "auto",
                  lineHeight: 1.6,
                }}
              >
                How we collect, use, and protect your information.
              </Typography>
            </Box>
          </motion.div>

          {/* Content card with animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: "4px",
                background: alpha(
                  theme.palette.background.paper,
                  isDark ? 0.6 : 0.92,
                ),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                boxShadow: isDark
                  ? `0 24px 64px ${alpha(theme.palette.common.black, 0.35)}`
                  : `0 24px 56px ${alpha(theme.palette.common.black, 0.08)}`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 5,
                  height: "100%",
                  background: `linear-gradient(180deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                  borderRadius: "5px 0 0 5px",
                },
                "& .blog-markdown-p, & p": {
                  ...regularFont,
                  fontSize: getRelativeFontSize(2),
                  color: theme.palette.text.secondary,
                  lineHeight: 1.75,
                  m: 0,
                },
                "& .blog-markdown-p:last-child, & p:last-child": { mb: 0 },
                "& .blog-markdown-h2, & h2": {
                  ...mediumFont,
                  fontWeight: 700,
                  fontSize: getRelativeFontSize(6),
                  color: theme.palette.text.primary,
                  m: 0,
                  pb: 1,
                  borderBottom: `2px solid ${alpha(BRAND, 0.35)}`,
                  letterSpacing: "0.02em",
                },
                "& .blog-markdown-h2:first-of-type, & h2:first-of-type": {
                  mt: 0,
                },
                "& .blog-markdown-ul, & ul": {
                  pl: 2.5,
                  m: 0,
                },
                "& .blog-markdown-ul li, & ul li": {
                  ...regularFont,
                  fontSize: getRelativeFontSize(2),
                  color: theme.palette.text.secondary,
                  lineHeight: 1.7,
                  m: 0,
                },
                "& strong": {
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                },
                "& .blog-markdown-p em, & p em": {
                  fontStyle: "normal",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  letterSpacing: "0.01em",
                  m: 0,
                },
                "& .blog-markdown-link, & a": {
                  color: BRAND,
                  textDecoration: "underline",
                },
                "& .blog-markdown-link:hover, & a:hover": {
                  textDecoration: "none",
                },
              }}
            >
              <Box
                component="div"
                sx={{ pl: 2 }}
                dangerouslySetInnerHTML={{
                  __html: formatDescriptionMarkdown(PRIVACY_CONTENT),
                }}
              />
            </Box>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy;
